import styled, { css, keyframes } from 'styled-components';

import { YatSprite } from '@pkg/components/YatSprite';

export const DesktopWrapper = styled.div`
    flex-direction: column;
    gap: 30px;
    flex-shrink: 0;
    align-self: center;

    display: none;
    width: 100%;
    overflow: hidden;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        display: flex;
    }
`;

export const MobileWrapper = styled.div`
    width: 100%;
    padding-top: 10px;
    padding-bottom: 10px;
    position: relative;

    display: flex;
    flex-direction: column;
    gap: 5px;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        display: none;
    }
`;

export const InfoSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const OwnerText = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;

    font-weight: 700;
    font-size: 12px;
    line-height: 12px;
    letter-spacing: -0.02em;
    margin-bottom: 10px;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 18px;
        line-height: 18px;
    }
`;

export const OwnerIcon = styled(YatSprite)`
    position: relative;
    top: -1px;
    font-size: 12px;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 20px;
    }
`;

export const MainTitle = styled.div`
    font-weight: 800;
    font-size: 40px;
    line-height: 40px;
    color: #ffffff;

    display: flex;
    gap: 10px;
    align-items: center;

    margin-bottom: 2px;
    gap: 5px;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        gap: 10px;
    }
`;

export const AssetName = styled.div`
    color: #ffffff;
    font-weight: 900;
    font-size: 18px;
    line-height: 100%;
    min-width: 125px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-bottom: 5px;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-weight: 800;
        font-size: 40px;
        line-height: 40px;
    }
`;

export const HoverUnderline = styled.div`
    height: 2px;
    width: 100%;
    background-color: #fff;
    position: absolute;
    top: 100%;
    left: 1px;

    transform: scaleX(0);
    transform-origin: right 0;
    transition: transform 300ms ease;

    display: none;
    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        display: block;
    }
`;

export const HoverArrow = styled.div`
    width: 22px;
    height: 22px;
    background-color: #fff;
    border-radius: 100%;

    transition: transform 300ms ease;

    align-items: center;
    justify-content: center;
    color: #090719;

    margin-left: 4px;

    transform: scale(0);
    transform-origin: center center;
    transition: transform 300ms ease;

    align-self: flex-end;

    position: absolute;
    left: 100%;
    top: 0;
    margin-left: 9px;

    svg {
        width: 14.61px;
        height: 16px;
        opacity: 0;
        transform: translateX(-6px);
        transition:
            transform 300ms ease,
            opacity 300ms ease;
    }

    display: none;
    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        display: flex;
    }
`;

const hoverAnimation = keyframes`
    0% { transform: scaleX(0); }
    100% { transform: scaleX(1); transform-origin: 0 0; }
`;

export const CollectionName = styled.div`
    font-weight: 500;
    font-size: 12px;
    line-height: 12px;
    letter-spacing: -0.03em;

    color: #fff;
    margin: 0;
    padding-top: 5px;
    a {
        transition: color 0.2s ease-in-out;
        position: relative;
    }

    ${HoverUnderline} {
        transform: scaleX(1);
        transform-origin: 0 0;
    }

    a:hover {
        ${HoverUnderline} {
            animation: ${hoverAnimation};
            animation-duration: 400ms;
        }

        ${HoverArrow} {
            transform: scale(1);
            transition-delay: 200ms;

            svg {
                opacity: 1;
                transform: translateX(0);
                transition-delay: 400ms;
            }
        }
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 16px;
        line-height: 16px;
    }
`;

export const Rank = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;

    svg {
        opacity: 0.5;
    }
`;

export const RankTag = styled.div`
    background: rgba(0, 0, 0, 0.3);
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    padding: 3px;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        height: 25px;
        padding: 6px;
    }
`;

export const RankTagText = styled.div`
    font-weight: 900;
    font-size: 10px;
    line-height: 100%;
    letter-spacing: -0.03em;

    white-space: nowrap;

    background: linear-gradient(94.36deg, #ffcf53 6.76%, #ff8a00 67.4%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 14px;
    }
`;

export const TextSubtle = styled.div`
    font-weight: 500;
    font-size: 12px;
    line-height: 14px;
    letter-spacing: -0.03em;
    color: rgba(255, 255, 255, 0.5);
`;

export const YatRow = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
`;

export const MobileYatRow = styled.div`
    margin-top: 5px;
`;

/* Prices Section
---------------------------------- */

export const PricesSection = styled.div<{ $showActivitySideBar?: boolean }>`
    display: flex;
    gap: 10px;
    width: 100%;
    flex-wrap: wrap;
`;

/* Buttons Section
---------------------------------- */

export const ButtonsSection = styled.div<{ $newDetailViewWIP?: boolean }>`
    display: flex;
    gap: 10px;

    position: absolute;
    top: 0;
    right: 0;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        top: unset;
        bottom: 0;
        margin-top: 0;
        position: relative;
    }

    ${({ $newDetailViewWIP }) => {
        if ($newDetailViewWIP) {
            return css`
                top: unset;
                right: unset;
                bottom: 0;
                left: 0;
            `;
        }
    }}
`;

export const MobileButtonSection = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 20px;
`;

export const OwnersTotal = styled.div`
    font-family: 'Alliance No.1';
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    line-height: 120%;
    letter-spacing: -0.03em;
    color: #ffa361;
    span {
        font-weight: 400;
        color: rgba(255, 255, 255, 0.5);
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 22px;
    }
`;

/* Actions Section
---------------------------------- */
export const Actions = styled.div``;
